import {
  pollCommandAccessToken,
  requestCommandDeviceAuthorization,
} from "../environments/primary/cmmdConnectionClient";
import { CmmdForgeRuntimeApiClient } from "../environments/primary/runtimeConnectionClient";
import { reloadDesktopRendererAfterBridgeUpdate } from "./desktopRendererRebootstrap";
import { buildCmmdRuntimeEnvironmentId } from "../environments/cmmdRuntimeEnvironmentId";

const DEFAULT_CMMD_CONTROL_PLANE_HTTP_URL = "https://cmmd.ai";

export function resolveCmmdControlPlaneHttpUrl(): string {
  return import.meta.env.VITE_COMMAND_APP_URL?.trim() || DEFAULT_CMMD_CONTROL_PLANE_HTTP_URL;
}

function parsePositiveInteger(value: string | undefined): number | null {
  if (!value) return null;
  if (!/^\d+$/.test(value)) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function resolveDesktopCloudCmmdProjectId(): number {
  const bootstrap = window.desktopBridge?.getLocalEnvironmentBootstrap?.();
  const persistedProjectId =
    bootstrap?.primaryMode === "cloud" ? bootstrap.cmmdProjectId?.trim() : undefined;
  const projectId =
    parsePositiveInteger(persistedProjectId) ??
    parsePositiveInteger(import.meta.env.VITE_FORGE_CLOUD_CMMD_PROJECT_ID?.trim());
  if (!projectId) {
    throw new Error(
      "Cloud mode requires a CMMD project id. Link a CMMD project before connecting to a cloud runtime.",
    );
  }
  return projectId;
}

function resolveDesktopCloudCmmdOrganizationId(): number | undefined {
  const bootstrap = window.desktopBridge?.getLocalEnvironmentBootstrap?.();
  if (bootstrap?.primaryMode !== "cloud") return undefined;
  return parsePositiveInteger(bootstrap.cmmdOrganizationId?.trim()) ?? undefined;
}

function resolveDesktopCloudOAuthHttpBaseUrl(): string {
  return resolveCmmdControlPlaneHttpUrl();
}

/**
 * Run the full device-OAuth flow to (re-)establish a desktop cloud runtime session.
 *
 * Steps:
 * 1. Request CMMD device authorization
 * 2. Open the verification URL in a browser popup
 * 3. Poll for an access token
 * 4. Ask CMMD to ensure/connect the project runtime
 * 5. Call desktopBridge.setBackendMode with the runtime descriptor
 *
 * This is the renderer fallback for older desktop bridges. Current desktop
 * builds expose `desktopAuth.refreshCloudSession`, which is the single source
 * of truth for CMMD-controlled runtime refresh because desktop owns persisted
 * CMMD identity and runtime tickets.
 */
export async function performDesktopCloudReAuth(signal: AbortSignal): Promise<void> {
  const bridge = window.desktopBridge;
  if (bridge?.desktopAuth?.refreshCloudSession) {
    await bridge.desktopAuth.refreshCloudSession();
    reloadDesktopRendererAfterBridgeUpdate();
    return;
  }
  if (!bridge?.setBackendMode) {
    throw new Error("Cloud mode is not available in this version of Forge.");
  }

  const oauthEndpoint = { httpBaseUrl: resolveDesktopCloudOAuthHttpBaseUrl() };
  const deviceAuthorization = await requestCommandDeviceAuthorization(signal, oauthEndpoint);

  window.open(
    deviceAuthorization.verification_uri_complete,
    "forge-command-oauth",
    "popup=true,width=540,height=720",
  );

  const tokenResult = await pollCommandAccessToken(deviceAuthorization, signal, oauthEndpoint);
  const projectId = resolveDesktopCloudCmmdProjectId();
  const runtimeApi = new CmmdForgeRuntimeApiClient({
    cmmdHttpBaseUrl: resolveCmmdControlPlaneHttpUrl(),
    accessToken: tokenResult.access_token,
  });
  const organizationId = resolveDesktopCloudCmmdOrganizationId();
  const runtime = await runtimeApi.ensureRuntime({
    projectId,
    ...(organizationId !== undefined ? { organizationId } : {}),
    signal,
  });
  const descriptor = await runtimeApi.connectRuntime({
    runtimeId: runtime.runtimeId,
    ...(organizationId !== undefined ? { organizationId } : {}),
    signal,
  });

  await bridge.setBackendMode({
    mode: "cloud",
    bearerToken: descriptor.bearerToken,
    cloudRecord: {
      httpBaseUrl: descriptor.httpBaseUrl,
      wsBaseUrl: descriptor.wsBaseUrl,
      environmentId: buildCmmdRuntimeEnvironmentId(descriptor.runtime.runtimeId),
      label: `Forge Cloud Project ${descriptor.runtime.projectId}`,
      cmmdProjectId: String(descriptor.runtime.projectId),
      ...(organizationId !== undefined ? { cmmdOrganizationId: String(organizationId) } : {}),
      runtimeId: descriptor.runtime.runtimeId,
      runtimeTicketExpiresAt: descriptor.expiresAt,
    },
  });
  // The desktop main process persists the cloud session and relaunches.
}

/**
 * Switch the desktop app back to local backend mode.
 *
 * Calls desktopBridge.setBackendMode({mode:"local"}) which clears the cloud
 * bearer and relaunches the app in local mode. Safe to call when the bridge
 * is absent (browser) — resolves as a no-op.
 *
 * Single source of truth for the "switch to local" bridge call — used by both
 * the Settings Disconnect button and CloudSessionExpiredCoordinator's
 * "Switch to Local" handler.
 */
export async function switchToLocalBackendMode(): Promise<void> {
  if (window.desktopBridge?.desktopAuth?.switchToLocal) {
    await window.desktopBridge.desktopAuth.switchToLocal();
    return;
  }
  await window.desktopBridge?.setBackendMode?.({ mode: "local" });
}
